import { useState, useEffect } from "react";
import { X, Settings, ShieldCheck } from "lucide-react";
import type { Organization, OrganizationType, OrganizationTier } from "../types";
import {
  ORG_TYPES,
  ORG_TYPE_LABEL_KEYS,
  ORG_TYPE_EMOJI,
  ORG_TIERS,
  ORG_TIER_LABELS,
  ORG_TIER_DESCRIPTION_KEYS,
} from "../constants";
import { OrganizationSSOPanel } from "../../organization-sso";
import { useLocale } from "../../../context/LanguageContext";
import { MutationErrorBanner } from "../../../components/ui/MutationErrorBanner";

type DrawerTab = "general" | "sso";

interface EditOrganizationDrawerProps {
  organization: Organization;
  onSave: (data: {
    name: string;
    type: OrganizationType;
    tier?: OrganizationTier;
    description?: string;
    website?: string;
  }) => void;
  onClose: () => void;
  loading: boolean;
  error?: string | null;
}

export function EditOrganizationDrawer({
  organization,
  onSave,
  onClose,
  loading,
  error,
}: EditOrganizationDrawerProps) {
  const { t } = useLocale();
  const [tab, setTab] = useState<DrawerTab>("general");
  const [name, setName] = useState(organization.name);
  const [type, setType] = useState<OrganizationType>(organization.type);
  const [tier, setTier] = useState<OrganizationTier>(organization.tier);
  const [description, setDescription] = useState(organization.description ?? "");
  const [website, setWebsite] = useState(organization.website ?? "");

  // Re-init when org changes (drawer reused across rows)
  useEffect(() => {
    setName(organization.name);
    setType(organization.type);
    setTier(organization.tier);
    setDescription(organization.description ?? "");
    setWebsite(organization.website ?? "");
    setTab("general");
  }, [organization]);

  const isNameValid = name.trim().length >= 2 && name.trim().length <= 100;
  const canSubmit = isNameValid && !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSave({
      name: name.trim(),
      type,
      tier,
      description: description.trim() || undefined,
      website: website.trim() || undefined,
    });
  };

  const ssoFeatureFlagOn =
    import.meta.env.VITE_FEATURE_SSO_OIDC === "true";

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-2xl flex-col border-l border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {t("organizations.edit.title")}
            </h2>
            <p className="text-xs text-muted-foreground">{organization.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label={t("adminUI.aria.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div
          role="tablist"
          aria-label={t("adminUI.aria.organizationSections")}
          className="flex gap-1 border-b border-border px-6 pt-3"
        >
          <TabButton
            active={tab === "general"}
            onClick={() => setTab("general")}
            icon={<Settings className="h-4 w-4" />}
            label={t("organizations.edit.tabGeneral")}
          />
          {ssoFeatureFlagOn && (
            <TabButton
              active={tab === "sso"}
              onClick={() => setTab("sso")}
              icon={<ShieldCheck className="h-4 w-4" />}
              label="SSO"
            />
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "general" && (
            <GeneralTab
              organization={organization}
              name={name}
              setName={setName}
              type={type}
              setType={setType}
              tier={tier}
              setTier={setTier}
              description={description}
              setDescription={setDescription}
              website={website}
              setWebsite={setWebsite}
              isNameValid={isNameValid}
            />
          )}
          {tab === "sso" && ssoFeatureFlagOn && (
            <OrganizationSSOPanel organizationId={organization._id} />
          )}
        </div>

        {/* Error banner — only relevant for the General tab's save flow.
            SSO panel has its own save button + error handling. */}
        {tab === "general" && error && (
          <div className="border-t border-border px-6 pt-4">
            <MutationErrorBanner message={error} />
          </div>
        )}

        {/* Footer — only General tab has a global save; SSO tab has its own save button */}
        {tab === "general" && (
          <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? t("organizations.edit.saving") : t("organizations.edit.saveChanges")}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Tabs UI ──────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`inline-flex h-9 items-center gap-1.5 rounded-t-md border-b-2 px-3 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── General tab fields ───────────────────────────────────────────────────────

interface GeneralTabProps {
  organization: Organization;
  name: string;
  setName: (v: string) => void;
  type: OrganizationType;
  setType: (v: OrganizationType) => void;
  tier: OrganizationTier;
  setTier: (v: OrganizationTier) => void;
  description: string;
  setDescription: (v: string) => void;
  website: string;
  setWebsite: (v: string) => void;
  isNameValid: boolean;
}

function GeneralTab({
  organization,
  name,
  setName,
  type,
  setType,
  tier,
  setTier,
  description,
  setDescription,
  website,
  setWebsite,
  isNameValid,
}: GeneralTabProps) {
  const { t } = useLocale();
  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label
          htmlFor="edit-org-name"
          className="block text-sm font-medium text-foreground"
        >
          {t("organizations.field.orgName")} <span className="text-destructive">*</span>
        </label>
        <input
          id="edit-org-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {name.length > 0 && !isNameValid && (
          <p className="mt-1 text-xs text-destructive">
            {t("organizations.validation.nameLength")}
          </p>
        )}
      </div>

      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-foreground">
          {t("organizations.field.orgType")}
        </label>
        <div className="mt-1.5 flex gap-2">
          {ORG_TYPES.map((orgType) => (
            <button
              key={orgType}
              type="button"
              onClick={() => setType(orgType)}
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                type === orgType
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <span className="mr-1.5">{ORG_TYPE_EMOJI[orgType]}</span>
              {t(ORG_TYPE_LABEL_KEYS[orgType])}
            </button>
          ))}
        </div>
      </div>

      {/* Tier */}
      <div>
        <label className="block text-sm font-medium text-foreground">
          {t("organizations.field.planTier")}
        </label>
        <div className="mt-1.5 flex gap-2">
          {ORG_TIERS.map((orgTier) => (
            <button
              key={orgTier}
              type="button"
              onClick={() => setTier(orgTier)}
              className={`flex-1 rounded-md border px-3 py-2 text-center transition-colors ${
                tier === orgTier
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <span className="block text-sm font-medium">
                {ORG_TIER_LABELS[orgTier]}
              </span>
              <span className="block text-[10px] text-muted-foreground">
                {t(ORG_TIER_DESCRIPTION_KEYS[orgTier])}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="edit-org-description"
          className="block text-sm font-medium text-foreground"
        >
          {t("organizations.field.description")}
        </label>
        <textarea
          id="edit-org-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("adminUI.organizations.editDescriptionPlaceholder")}
          rows={3}
          maxLength={2000}
          className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Website */}
      <div>
        <label
          htmlFor="edit-org-website"
          className="block text-sm font-medium text-foreground"
        >
          {t("organizations.field.website")}
        </label>
        <input
          id="edit-org-website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://example.com"
          maxLength={200}
          className="mt-1.5 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* Metadata (read-only) */}
      <div className="rounded-md bg-muted/50 p-4">
        <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t("organizations.edit.infoHeading")}
        </h4>
        <dl className="mt-2 space-y-1.5 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t("organizations.field.id")}</dt>
            <dd className="font-mono text-xs text-foreground">
              {organization._id}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t("organizations.field.members")}</dt>
            <dd className="text-foreground">{organization.memberCount}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t("organizations.field.status")}</dt>
            <dd
              className={
                organization.isActive ? "text-success" : "text-destructive"
              }
            >
              {organization.isActive
                ? t("organizations.status.active")
                : t("organizations.status.suspended")}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
